import { makeHeartbeatWS } from '@solid-primitives/websocket';
import { createDisposable } from '@solid-primitives/rootless';
import { filter } from 'lodash-es';

import type { MessageContent } from '../proto/notification';
import type { BusinessMessage } from '../type';
import { createSharedRoot } from '../../../request/src/globalStore';
import makeReconnectingWS from '../lib/makeReconnectingWS';
import MessageCodec from '../proto/messageCodec';
import { getSockUrl, makeSocketIdentity, normalizeInboundWsPayload } from '../utils';

type SocketAPI = {
  send: (msg: Record<string, any>) => void;
  onEvent: (params: Partial<BusinessMessage>, callback: (msg: BusinessMessage) => void) => VoidFunction;
  onReconnect: (callback: (retryCount?: number) => void) => void;
};

/** Blob/ArrayBuffer 内是否为 UTF-8 JSON 文本（避免把 protobuf 二进制误用 text() 解码成乱码） */
function bytesLookLikeJsonText(u8: Uint8Array): boolean {
  let i = 0;
  if (u8.length >= 3 && u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf) i = 3;
  while (i < u8.length && (u8[i] === 0x20 || u8[i] === 0x09 || u8[i] === 0x0a || u8[i] === 0x0d)) i++;
  if (i >= u8.length) return false;
  const b = u8[i];
  return b === 0x7b || b === 0x5b;
}

/** 初始化 socket */
const useSocket = createSharedRoot<SocketAPI>(() => {
  if (import.meta.env.SSR) {
    return {
      send: () => void 0,
      onEvent: () => {
        return () => void 0;
      },
      onReconnect: () => void 0,
    };
  }

  /** 消息编解码器 */
  const messageCodec = new MessageCodec(true);

  /** 重连次数 */
  const [retryCount, setRetryCount] = createSignal(0);

  /** 重连发送队列 */
  const sendQueue: Array<() => void> = [];

  /**
   * @see @solid-primitives/websocket
   * [https://github.com/solidjs-community/solid-primitives/blob/927551665b895d7d22adec45da54cf7473bd8af9/packages/websocket/src/index.ts#L187C14-L187C29]
   */
  const ws = makeHeartbeatWS(
    makeReconnectingWS(
      () => {
        const _wsUrl = getSockUrl();
        const separator = _wsUrl.includes('?') ? '&' : '?';

        return `${_wsUrl}${separator}${makeSocketIdentity()}`;
      },
      undefined,
      {
        delay: 10000,
        onRetry: () => setRetryCount((prev) => prev + 1),
      }
    ),
    // 心跳间隔，发送ping之后，如果pong（1500ms）超时，则重连
    {
      interval: 5000, // 5s 一次 ping
      wait: 15000, // 15s 内没收到 pong 则重连
    }
  );

  /**
   * 连接建立后，发送队列中的消息
   */
  ws.addEventListener('open', () => {
    while (sendQueue.length) {
      sendQueue.shift()?.();
    }
  });

  const [latestMsgs, setLatestMsgs] = createSignal<BusinessMessage[]>([]);
  /**
   * 消息处理函数
   * - `string`：按 JSON 文本解析（心跳等非 JSON 仅打短日志）。
   * - `Blob`：**不能**一律 `blob.text()`——若为 protobuf 二进制会得到乱码；先 `arrayBuffer()`，若以 `{`/`[` 开头再当 UTF-8 JSON。
   * - `ArrayBuffer`：同上，非 JSON 字形则走 protobuf 解码。
   */
  ws.addEventListener('message', async ({ data }) => {
    if (data == null) return;

    const applyJsonPayload = (jsonData: unknown) => {
      const normalized = normalizeInboundWsPayload(jsonData);
      if (normalized != null && normalized.length > 0) {
        setLatestMsgs(normalized);
      } else {
        setLatestMsgs([jsonData as BusinessMessage]);
      }
    };

    if (typeof data === 'string') {
      try {
        applyJsonPayload(JSON.parse(data));
      } catch {
        const preview = data.length > 120 ? `${data.slice(0, 120)}…` : data;
        console.log('收到非 JSON 文本帧（如心跳）:', preview);
      }
      return;
    }

    const uint8Array =
      data instanceof Blob ? new Uint8Array(await data.arrayBuffer()) : new Uint8Array(data as ArrayBuffer);

    if (bytesLookLikeJsonText(uint8Array)) {
      const text = new TextDecoder('utf-8', { fatal: false }).decode(uint8Array);
      try {
        applyJsonPayload(JSON.parse(text));
      } catch (e) {
        console.warn('JSON 文本帧解析失败', e);
      }
      return;
    }

    try {
      const isCompressed = messageCodec.isCompressed(uint8Array);

      let msgData: MessageContent | string = isCompressed
        ? messageCodec.decode(uint8Array)
        : new TextDecoder().decode(uint8Array);
      msgData = typeof msgData === 'string' ? (JSON.parse(msgData) as MessageContent) : msgData;

      const wsData = normalizeInboundWsPayload(msgData);
      if (wsData != null && wsData.length > 0) {
        setLatestMsgs(wsData);
      }
    } catch (error) {
      console.error('Protobuf解析失败:', error);
    }
  });

  return {
    send: (msg) => {
      if (!ws) return;

      const strMsg = JSON.stringify({
        ...msg,
        business: 'BZ',
        content: 'test',
      });
      ws.readyState === 1 ? ws.send(strMsg) : sendQueue.push(() => ws.send(strMsg));
    },
    onEvent: (params, callback) => {
      const dispose = createDisposable((_dispose) => {
        createEffect(
          on(
            latestMsgs,
            (data) => {
              /**
               * 遍历latestMsgs数组，
               * 根据params参数，匹配出符合条件的消息，并调用回调函数
               */
              const matched = filter(data, params);
              matched.length > 0 && callback(matched[matched.length - 1]);
            },
            { defer: true }
          )
        );
      });

      onCleanup(dispose);

      return dispose;
    },
    onReconnect: (cb) => {
      const dispose = createDisposable(() => {
        createEffect(on(retryCount, (counts) => cb(counts), { defer: true }));
      });

      onCleanup(dispose);

      return dispose;
    },
  };
});

export default useSocket;
